import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Printer, ArrowRightLeft, Settings, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const FILTERS = [
  { v: "all", label: "Semua" },
  { v: "top", label: "Top 30" },
  { v: "reg", label: "Reguler" },
];

export default function WarehouseMonitor() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [source, setSource] = useState("");
  const [dest, setDest] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("prepare");
  const [selected, setSelected] = useState({});
  const [setOpen, setSetOpen] = useState(false);
  const [form, setForm] = useState({ top_trigger: 5, top_target: 10, reg_trigger: 2, reg_target: 3 });

  useEffect(() => {
    api.get("/warehouses", { params: { usage: "produk_jadi" } }).then((r) => setWarehouses(r.data)).catch(() => {});
  }, []);

  const load = async (src, dst) => {
    setLoading(true);
    try {
      const params = {};
      if (src) params.source = src;
      if (dst) params.dest = dst;
      const r = await api.get("/transfers/replenishment", { params });
      setData(r.data);
      setSource(r.data.source);
      setDest(r.data.dest);
      setForm(r.data.settings);
      // auto-select semua baris "perlu disiapkan"
      const sel = {};
      (r.data.rows || []).filter((x) => x.bucket === "prepare").forEach((x) => { sel[x.variant_id] = true; });
      setSelected(sel);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* default Gudang Baru → Utama dari backend */ }, []); // eslint-disable-line

  const onChangeWh = (which, val) => {
    const src = which === "src" ? val : source;
    const dst = which === "dst" ? val : dest;
    if (src && dst && src === dst) return toast.error("Sumber & tujuan tidak boleh sama");
    load(src, dst);
  };

  const rows = data?.rows || [];
  const applyFilter = (list) => list.filter((r) => {
    if (filter === "top" && !r.is_top) return false;
    if (filter === "reg" && r.is_top) return false;
    if (q) {
      const s = `${r.product_name} ${r.color} ${r.size} ${r.sku || ""}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  const prepareRows = useMemo(() => applyFilter(rows.filter((r) => r.bucket === "prepare")), [rows, filter, q]);
  const cannotRows = useMemo(() => applyFilter(rows.filter((r) => r.bucket === "cannot")), [rows, filter, q]);
  const inTransit = data?.in_transit || [];

  const toggle = (vid) => setSelected((s) => ({ ...s, [vid]: !s[vid] }));
  const allChecked = prepareRows.length > 0 && prepareRows.every((r) => selected[r.variant_id]);
  const toggleAll = () => {
    const nv = !allChecked;
    setSelected((s) => { const c = { ...s }; prepareRows.forEach((r) => { c[r.variant_id] = nv; }); return c; });
  };

  const chosen = prepareRows.filter((r) => selected[r.variant_id]);

  const createTransfer = () => {
    if (!chosen.length) return toast.error("Pilih minimal 1 varian untuk disiapkan");
    navigate("/transfers/create", {
      state: {
        from: source, to: dest,
        items: chosen.map((r) => ({ variant_id: r.variant_id, product_name: r.product_name, color: r.color, size: r.size, qty: r.prepare })),
      },
    });
  };

  const printPicking = () => {
    const list = chosen.length ? chosen : prepareRows;
    if (!list.length) return toast.error("Tidak ada baris untuk dicetak");
    const w = window.open("", "_blank");
    const rowsHtml = list.map((r, i) => `<tr><td>${i + 1}</td><td>${r.product_name}</td><td>${r.color || "-"}/${r.size || "-"}</td><td>${r.sku || "-"}</td><td style="text-align:center">${r.prepare}</td></tr>`).join("");
    w.document.write(`<html><head><title>Picking List</title><style>body{font-family:sans-serif;padding:24px}h2{margin:0 0 4px}p{margin:0 0 12px;color:#555;font-size:13px}table{width:100%;border-collapse:collapse;font-size:13px}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}th{background:#f3f3f3}</style></head><body><h2>Picking List — Pantau Stok Gudang</h2><p>${data?.source_name || ""} → ${data?.dest_name || ""} · ${new Date().toLocaleString("id-ID")}</p><table><thead><tr><th>No</th><th>Produk</th><th>Warna/Ukuran</th><th>SKU</th><th>Qty Siapkan</th></tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const saveSettings = async () => {
    try {
      await api.put("/transfers/replenishment/settings", {
        top_trigger: Number(form.top_trigger), top_target: Number(form.top_target),
        reg_trigger: Number(form.reg_trigger), reg_target: Number(form.reg_target),
        top_n: data?.settings?.top_n || 30, period: data?.settings?.period || 30,
      });
      toast.success("Pengaturan tersimpan");
      setSetOpen(false);
      load(source, dest);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sm = data?.summary || {};
  const whOpts = warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>);

  const PriorityBadge = ({ r }) => (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${r.is_top ? "bg-cocoa text-ivory" : "bg-taupe/20 text-cocoa"}`} data-testid={`prio-${r.variant_id}`}>{r.priority}{r.rank ? ` #${r.rank}` : ""}</span>
  );

  return (
    <div data-testid="warehouse-monitor-page">
      <PageHeader title="Pantau Stok Gudang" subtitle="Daftar varian yang perlu disiapkan untuk dipindahkan ke gudang tujuan">
        <Button variant="outline" className="border-taupe text-cocoa" onClick={() => load(source, dest)} data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Muat Ulang</Button>
        <Button variant="outline" className="border-taupe text-cocoa" onClick={() => setSetOpen(true)} data-testid="settings-btn"><Settings className="w-4 h-4 mr-1.5" />Pengaturan</Button>
      </PageHeader>

      <Panel className="p-4 mb-4">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_2fr] items-end">
          <div>
            <Label className="text-xs">Gudang Sumber</Label>
            <Select value={source} onValueChange={(v) => onChangeWh("src", v)}>
              <SelectTrigger className="bg-white" data-testid="source-select"><SelectValue placeholder="Pilih gudang sumber" /></SelectTrigger>
              <SelectContent>{whOpts}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Gudang Tujuan</Label>
            <Select value={dest} onValueChange={(v) => onChangeWh("dst", v)}>
              <SelectTrigger className="bg-white" data-testid="dest-select"><SelectValue placeholder="Pilih gudang tujuan" /></SelectTrigger>
              <SelectContent>{whOpts}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Cari Produk / SKU</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-taupe" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nama produk / warna / ukuran / SKU" className="bg-white pl-9" data-testid="search-input" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          {FILTERS.map((f) => (
            <Button key={f.v} size="sm" variant={filter === f.v ? "default" : "outline"} onClick={() => setFilter(f.v)} className={filter === f.v ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"} data-testid={`filter-${f.v}`}>{f.label}</Button>
          ))}
        </div>
      </Panel>

      <div className="grid gap-3 sm:grid-cols-3 mb-4">
        <Panel className="p-4" data-testid="summary-prepare"><div className="text-xs text-wmuted">Varian Perlu Disiapkan</div><div className="text-2xl font-bold text-cocoa">{sm.prepare_variants ?? 0}</div></Panel>
        <Panel className="p-4" data-testid="summary-pcs"><div className="text-xs text-wmuted">Total Pcs Disiapkan</div><div className="text-2xl font-bold text-cocoa">{sm.prepare_pcs ?? 0}</div></Panel>
        <Panel className="p-4" data-testid="summary-cannot"><div className="text-xs text-wmuted">Varian Tidak Bisa Dipenuhi</div><div className="text-2xl font-bold text-status-rejected">{sm.cannot_variants ?? 0}</div></Panel>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex gap-2">
          <Button size="sm" variant={tab === "prepare" ? "default" : "outline"} onClick={() => setTab("prepare")} className={tab === "prepare" ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"} data-testid="tab-prepare">Perlu Disiapkan ({prepareRows.length})</Button>
          <Button size="sm" variant={tab === "transit" ? "default" : "outline"} onClick={() => setTab("transit")} className={tab === "transit" ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"} data-testid="tab-transit">Dalam Perpindahan ({inTransit.length})</Button>
          <Button size="sm" variant={tab === "cannot" ? "default" : "outline"} onClick={() => setTab("cannot")} className={tab === "cannot" ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"} data-testid="tab-cannot">Tidak Bisa Dipenuhi ({cannotRows.length})</Button>
        </div>
        {tab === "prepare" && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-taupe text-cocoa" onClick={printPicking} data-testid="print-btn"><Printer className="w-4 h-4 mr-1.5" />Cetak Picking List</Button>
            <Button size="sm" className="bg-cocoa text-ivory" onClick={createTransfer} data-testid="create-transfer-btn"><ArrowRightLeft className="w-4 h-4 mr-1.5" />Buat Perpindahan ({chosen.length})</Button>
          </div>
        )}
      </div>

      <Panel className="overflow-x-auto">
        {loading ? (
          <div className="py-10 text-center text-wmuted text-sm" data-testid="loading">Memuat...</div>
        ) : tab === "transit" ? (
          <Table data-testid="transit-table">
            <TableHeader><TableRow>
              <TableHead>No. Transfer</TableHead><TableHead>Produk</TableHead><TableHead>Warna/Ukuran</TableHead>
              <TableHead className="text-right">Qty</TableHead><TableHead>Asal → Tujuan</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {inTransit.length === 0 ? <TableRow><TableCell colSpan={6} className="py-8 text-center text-wmuted text-sm">Tidak ada transfer aktif ke gudang tujuan</TableCell></TableRow> :
                inTransit.map((t, i) => (
                  <TableRow key={i} data-testid={`transit-row-${i}`}>
                    <TableCell className="font-mono text-xs">{t.number}</TableCell>
                    <TableCell>{t.product_name}</TableCell>
                    <TableCell>{t.color || "-"}/{t.size || "-"}</TableCell>
                    <TableCell className="text-right">{t.qty}</TableCell>
                    <TableCell className="text-xs">{t.from_warehouse_name} → {t.to_warehouse_name}</TableCell>
                    <TableCell><span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800">{t.status}</span></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        ) : (
          <Table data-testid={tab === "prepare" ? "prepare-table" : "cannot-table"}>
            <TableHeader><TableRow>
              {tab === "prepare" && <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" /></TableHead>}
              <TableHead>Produk</TableHead><TableHead>Warna/Ukuran</TableHead><TableHead>Prioritas</TableHead>
              <TableHead className="text-right">Stok Tujuan</TableHead><TableHead className="text-right">Stok Sumber</TableHead>
              <TableHead className="text-right">Transfer Masuk</TableHead>
              {tab === "prepare" ? <TableHead className="text-right">Siapkan</TableHead> : <TableHead className="text-right">Kebutuhan</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {(tab === "prepare" ? prepareRows : cannotRows).length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-wmuted text-sm">{tab === "prepare" ? "Tidak ada varian yang perlu disiapkan" : "Tidak ada varian yang gagal dipenuhi"}</TableCell></TableRow>
              ) : (tab === "prepare" ? prepareRows : cannotRows).map((r) => (
                <TableRow key={r.variant_id} data-testid={`row-${r.variant_id}`}>
                  {tab === "prepare" && <TableCell><Checkbox checked={!!selected[r.variant_id]} onCheckedChange={() => toggle(r.variant_id)} data-testid={`check-${r.variant_id}`} /></TableCell>}
                  <TableCell><div className="font-medium text-cocoa">{r.product_name}</div><div className="text-[11px] text-wmuted">{r.sku || "tanpa SKU"}</div></TableCell>
                  <TableCell>{r.color || "-"}/{r.size || "-"}</TableCell>
                  <TableCell><PriorityBadge r={r} /></TableCell>
                  <TableCell className={`text-right ${r.dest_available === 0 ? "text-status-rejected font-semibold" : ""}`}>{r.dest_available}</TableCell>
                  <TableCell className="text-right">{r.source_available}</TableCell>
                  <TableCell className="text-right">{r.incoming || 0}</TableCell>
                  {tab === "prepare" ? (
                    <TableCell className="text-right font-semibold text-cocoa">{r.prepare}{r.shortage > 0 ? <span className="text-[11px] text-status-rejected font-normal"> (−{r.shortage})</span> : null}</TableCell>
                  ) : (
                    <TableCell className="text-right font-semibold text-status-rejected">{r.need}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent data-testid="settings-dialog">
          <DialogHeader><DialogTitle>Pengaturan Pemicu & Target</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Top 30 — Pemicu (≤)</Label><Input type="number" value={form.top_trigger} onChange={(e) => setForm({ ...form, top_trigger: e.target.value })} data-testid="set-top-trigger" /></div>
              <div><Label className="text-xs">Top 30 — Target Isi</Label><Input type="number" value={form.top_target} onChange={(e) => setForm({ ...form, top_target: e.target.value })} data-testid="set-top-target" /></div>
              <div><Label className="text-xs">Reguler — Pemicu (≤)</Label><Input type="number" value={form.reg_trigger} onChange={(e) => setForm({ ...form, reg_trigger: e.target.value })} data-testid="set-reg-trigger" /></div>
              <div><Label className="text-xs">Reguler — Target Isi</Label><Input type="number" value={form.reg_target} onChange={(e) => setForm({ ...form, reg_target: e.target.value })} data-testid="set-reg-target" /></div>
            </div>
            <p className="text-[11px] text-wmuted">Jika stok tersedia di gudang tujuan ≤ angka pemicu, sistem merekomendasikan pengisian sampai angka target.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-taupe text-cocoa" onClick={() => setSetOpen(false)} data-testid="set-cancel">Batal</Button>
            <Button className="bg-cocoa text-ivory" onClick={saveSettings} data-testid="set-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
